<script lang="ts" setup>
import type { ResponseResult } from '~kui/types/formResult';

import { Button } from '~ds/components/ui/button';
import { Text } from '~ds/components/ui/typography';
import { defineField, defineFormConfig } from '~kui/components/KUI/Core/Form/util/form';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

interface Props {
  email: string;
  initialCode?: string;
  result?: ResponseResult | null;
  loading?: boolean;
  isResent?: boolean;
  resendLoading?: boolean;
  /** Bumped by the parent on a successful resend to remount/clear the pincode. */
  resendNonce?: number;
}

const props = withDefaults(defineProps<Props>(), {
  initialCode: '',
  result: null,
  loading: false,
  isResent: false,
  resendLoading: false,
  resendNonce: 0,
});

const emit = defineEmits<{
  submit: [code: string];
  resend: [];
  changeEmail: [];
}>();

const { t } = useI18n();

const hasPrefilledCode = Boolean(props.initialCode);

const formConfig = defineFormConfig(
  {
    otpCode: defineField({
      type: 'pincode',
      options: {
        required: true,
        label: t('general.formFields.otpCode.label'),
        focusOnMounted: true,
        nextFocusOnSubmitButton: true,
      },
      initialValue: props.initialCode,
      hint: t('general.formFields.otpCode.hint'),
      schema: z.string()
        .length(6, t('general.formFields.otpCode.tooShort'))
        .regex(/^\d+$/, t('general.formFields.otpCode.onlyNumbers')),
    }),
  },
  t('general.auth.submitButton.submit'),
);

const handleSubmit = (value: z.infer<typeof formConfig.schema>) => {
  emit('submit', String(value.otpCode));
};
</script>

<template>
  <div data-testid="onboarding-otp-step">
    <KUICoreForm
      :key="resendNonce"
      :form-config="formConfig"
      :form-result="result"
      :loading="loading"
      :allow-clean-submit="hasPrefilledCode"
      @submit="handleSubmit"
    >
      <template #otpCode-leading>
        <div class="flex flex-col items-center">
          <KUICoreHtml
            class="text-center"
            :html="t('general.auth.enterOtpHelp', { email })"
          />
          <Button
            data-testid="onboarding-otp-change-email"
            class="mt-2"
            size="sm"
            variant="outline"
            type="button"
            :disabled="loading"
            @click="emit('changeEmail')"
          >
            {{ t('general.auth.changeEmailButton') }}
          </Button>
        </div>
      </template>
    </KUICoreForm>

    <div class="flex flex-col items-center space-y-2 mt-8">
      <Text
        v-if="isResent"
        size="lg"
        class="text-center"
      >
        {{ t('general.auth.resentOtpCode', { email }) }}
      </Text>
      <Button
        data-testid="onboarding-otp-resend"
        variant="outline"
        :loading="resendLoading"
        type="button"
        @click="emit('resend')"
      >
        {{ t('general.formFields.resendCode.label') }}
      </Button>
    </div>
  </div>
</template>
